/**
 * PB-/BK-Detail · Medien & Dokumente
 *
 * Upload-Bereich (Drag & Drop, mehrere Dateien, Kategorie je Datei), darunter
 * Suche + Kategorie-Filter, die Medien-Galerie (Bilder/Videos, gruppiert nach
 * Kategorie) und die Dokumentenliste (gruppiert nach Kategorie).
 *
 * Datenquelle: GET /v2/admin/users/{id}/media-documents (src/lib/mediaDocuments.ts).
 * Die Dateien liegen privat im Hetzner Object Storage; alle Links sind
 * kurzlebige presigned URLs, die useMediaDocuments regelmäßig erneuert.
 * Schreiben (Upload, Kategorie, Löschen) nur mit Permission users.write.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Icon } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { type V1User } from '../../../lib/v1';
import { usePermissions } from '../../../contexts/PermissionsContext';
import {
  DEFAULT_MEDIA_DOC_META,
  fileExtension,
  formatFileSize,
  mediaDocErrorMessage,
  mediaDocumentsApi,
  notifyMediaDocumentsChanged,
  type MediaDocItem,
  type MediaDocKind,
  type MediaDocMeta,
} from '../../../lib/mediaDocuments';
import { useMediaDocuments } from '../../../lib/useMediaDocuments';
import { MediaUploadPanel } from './medien/MediaUploadPanel';
import { MediaLightbox } from './medien/MediaLightbox';
import { DocusignPanel } from './medien/DocusignPanel';
import { categoryLabel, FileBadge, formatMediaDate, isPreviewable, PlayBadge, Spinner } from './medien/shared';

interface Props {
  user: V1User | null;
  dark: boolean;
}

type Filter = 'all' | `${MediaDocKind}:${string}`;

export function MedienTab({ user, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('users.write');
  const userId = user?.id ?? null;

  const { data, loading, error, reload } = useMediaDocuments(userId);
  const meta: MediaDocMeta = data?.meta ?? {
    ...DEFAULT_MEDIA_DOC_META,
    role: Number(user?.user_role) === 1 ? 'bk' : 'pb',
    categories: Number(user?.user_role) === 1
      ? { media: ['person', 'video'], documents: ['vertrag', 'referenz', 'lohnabrechnung', 'sonstiges'] }
      : DEFAULT_MEDIA_DOC_META.categories,
  };
  const media = data?.media ?? [];
  const documents = data?.documents ?? [];

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [lightbox, setLightbox] = useState<{ items: MediaDocItem[]; index: number } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  /** Kategorien in fester Reihenfolge + vorhandene Alt-Kategorien. */
  const groupsFor = (kind: MediaDocKind, rows: MediaDocItem[]) => {
    const base = kind === 'media' ? meta.categories.media : meta.categories.documents;
    const extra = Array.from(new Set(rows.map(r => r.kategorie))).filter(k => !base.includes(k));
    return [...base, ...extra];
  };
  const mediaGroups = useMemo(() => groupsFor('media', media), [media, meta]); // eslint-disable-line react-hooks/exhaustive-deps
  const docGroups = useMemo(() => groupsFor('document', documents), [documents, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = search.trim().toLocaleLowerCase();
  const matches = (item: MediaDocItem) =>
    (!q || item.name.toLocaleLowerCase().includes(q))
    && (filter === 'all' || filter === `${item.type}:${item.kategorie}`);
  const visibleMedia = media.filter(matches);
  const visibleDocs = documents.filter(matches);
  const filtering = q !== '' || filter !== 'all';
  const showMediaCard = filter === 'all' || filter.startsWith('media:');
  const showDocCard = filter === 'all' || filter.startsWith('document:');

  // Reihenfolge für Lightbox-Blättern = Anzeige-Reihenfolge
  const orderedMedia = mediaGroups.flatMap(g => visibleMedia.filter(m => m.kategorie === g));
  const orderedDocs = docGroups.flatMap(g => visibleDocs.filter(d => d.kategorie === g));

  const openItem = (item: MediaDocItem) => {
    const pool = (item.type === 'media' ? orderedMedia : orderedDocs).filter(isPreviewable);
    const index = pool.findIndex(p => p.id === item.id && p.type === item.type);
    if (index >= 0) {
      setLightbox({ items: pool, index });
    } else if (item.url) {
      window.open(item.download_url ?? item.url, '_blank', 'noopener,noreferrer');
    }
  };

  const changed = () => { if (userId != null) notifyMediaDocumentsChanged(userId); };

  const remove = async (item: MediaDocItem) => {
    if (!window.confirm(tr('pflege.media.delete_confirm', { name: item.name }))) return;
    setActionError(null);
    setBusyKey(`${item.type}:${item.id}`);
    try {
      await mediaDocumentsApi.remove(item.type, item.id);
      setLightbox(lb => {
        if (!lb) return lb;
        const items = lb.items.filter(i => !(i.id === item.id && i.type === item.type));
        return items.length ? { items, index: Math.min(lb.index, items.length - 1) } : null;
      });
      changed();
    } catch (err) {
      setActionError(mediaDocErrorMessage(err) ?? tr('pflege.media.delete_failed'));
    } finally {
      setBusyKey(null);
    }
  };

  const changeCategory = async (item: MediaDocItem, kategorie: string) => {
    if (kategorie === item.kategorie) return;
    setActionError(null);
    setBusyKey(`${item.type}:${item.id}`);
    try {
      const updated = await mediaDocumentsApi.update(item.type, item.id, { kategorie });
      setLightbox(lb => lb && {
        ...lb,
        items: lb.items.map(i => (i.id === item.id && i.type === item.type ? { ...i, ...updated } : i)),
      });
      changed();
    } catch (err) {
      setActionError(mediaDocErrorMessage(err) ?? tr('pflege.media.update_failed'));
    } finally {
      setBusyKey(null);
    }
  };

  const categorySelect = (item: MediaDocItem, variant: 'light' | 'overlay' = 'light') => {
    const groups = item.type === 'media' ? mediaGroups : docGroups;
    const options = groups.includes(item.kategorie) ? groups : [...groups, item.kategorie];
    const overlay = variant === 'overlay';
    return (
      <select
        aria-label={tr('pflege.media.category')}
        value={item.kategorie}
        disabled={busyKey === `${item.type}:${item.id}`}
        onClick={e => e.stopPropagation()}
        onChange={e => { void changeCategory(item, e.target.value); }}
        style={{
          border: `1px solid ${overlay ? 'rgba(255,255,255,0.18)' : t.border}`,
          background: overlay ? 'rgba(255,255,255,0.06)' : t.inputBg,
          color: overlay ? '#f2f4f7' : t.text,
          borderRadius: 8, fontSize: 12, padding: overlay ? '0 8px' : '5px 8px',
          height: overlay ? 32 : undefined, fontFamily: 'inherit', maxWidth: 200,
        }}
      >
        {options.map(slug => (
          <option key={slug} value={slug} style={overlay ? { color: '#101828' } : undefined}>
            {categoryLabel(tr, item.type, slug, meta.role)}
          </option>
        ))}
      </select>
    );
  };

  // Filter-Chips: alle Kategorien mit Treffern (ohne Kategorie-Filter gezählt).
  const searchHits = (item: MediaDocItem) => !q || item.name.toLocaleLowerCase().includes(q);
  type Chip = { key: Filter; label: string; count: number };
  const chips: Chip[] = ([
    { key: 'all', label: tr('pflege.media.filter_all'), count: media.filter(searchHits).length + documents.filter(searchHits).length },
    ...mediaGroups.map(slug => ({
      key: `media:${slug}` as Filter,
      label: categoryLabel(tr, 'media', slug, meta.role),
      count: media.filter(m => m.kategorie === slug && searchHits(m)).length,
    })),
    ...docGroups.map(slug => ({
      key: `document:${slug}` as Filter,
      label: categoryLabel(tr, 'document', slug, meta.role),
      count: documents.filter(d => d.kategorie === slug && searchHits(d)).length,
    })),
  ] as Chip[]).filter(c => c.key === 'all' || c.count > 0 || c.key === filter);

  const totalCount = media.length + documents.length;
  const lang = i18n.language;

  if (userId == null) return null;

  return (
    // Einspaltiges Grid: alle Karten untereinander in voller Breite (Batch 13).
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: isMobile ? 12 : 16, minWidth: 0 }}>
      {canWrite && (
        <MediaUploadPanel userId={userId} meta={meta} dark={dark} onUploaded={changed} />
      )}

      <DocusignPanel userId={userId} canWrite={canWrite} dark={dark} onSent={changed} />

      {/* Suche + Filter */}
      {totalCount > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ position: 'relative', maxWidth: isMobile ? '100%' : 360 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
              <Icon.Search size={16} c={t.placeholder} />
            </span>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={tr('pflege.media.search_placeholder')}
              aria-label={tr('pflege.media.search_placeholder')}
              style={{
                width: '100%', border: `1px solid ${t.border}`, borderRadius: 9,
                padding: '8px 12px 8px 34px', fontSize: 13, color: t.heading, background: t.inputBg,
                fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>
          <div role="group" aria-label={tr('common.action.filter')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chips.map(c => {
              const active = filter === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(active && c.key !== 'all' ? 'all' : c.key)}
                  style={{
                    border: `1px solid ${active ? BRAND : t.border}`,
                    background: active ? (dark ? '#2a1222' : BRAND_LIGHT) : t.card,
                    color: active ? BRAND : t.text,
                    borderRadius: 999, padding: '4px 11px', fontSize: 12, fontWeight: active ? 600 : 500,
                    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                  }}
                >
                  {c.label} <span style={{ color: active ? BRAND : t.muted, fontWeight: 500 }}>{c.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {actionError && (
        <div role="alert" style={{ fontSize: 12.5, color: '#b42318', background: '#fef3f2', border: '1px solid #fecdca', borderRadius: 8, padding: '8px 12px' }}>
          {actionError}
        </div>
      )}

      {error && !data ? (
        <Card dark={dark}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#b42318', flex: 1 }}>{tr('pflege.media.load_error')}</span>
            <Button variant="secondary" size="sm" dark={dark} onClick={() => { void reload(); }}>{tr('pflege.media.retry')}</Button>
          </div>
        </Card>
      ) : loading && !data ? (
        <Card dark={dark}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: t.muted, fontSize: 13 }}>
            <Spinner /> {tr('common.loading')}
          </div>
        </Card>
      ) : data && totalCount === 0 ? (
        <Card dark={dark}>
          <div style={{ textAlign: 'center', padding: '24px 8px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.heading }}>{tr('pflege.media.empty_all_title')}</div>
            <div style={{ fontSize: 13, color: t.muted, marginTop: 6 }}>
              {canWrite ? tr('pflege.media.empty_all_text') : tr('pflege.media.empty_all_text_readonly')}
            </div>
          </div>
        </Card>
      ) : data ? (
        <>
          {filtering && visibleMedia.length === 0 && visibleDocs.length === 0 && (
            <div style={{ fontSize: 13, color: t.muted, fontStyle: 'italic' }}>{tr('pflege.media.no_results')}</div>
          )}

          {/* Medien */}
          {showMediaCard && (!filtering || visibleMedia.length > 0) && (
            <Card dark={dark} style={{ padding: 0 }}>
              <WindowHeader dark={dark} title={tr('pflege.media.title_media')} count={visibleMedia.length} />
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {mediaGroups.map(slug => {
                  const items = visibleMedia.filter(m => m.kategorie === slug);
                  if (filtering && items.length === 0) return null;
                  return (
                    <section key={slug} aria-label={categoryLabel(tr, 'media', slug, meta.role)}>
                      <Subtitle dark={dark}>
                        {categoryLabel(tr, 'media', slug, meta.role)} <span style={{ color: t.muted, fontWeight: 500 }}>· {items.length}</span>
                      </Subtitle>
                      {items.length === 0 ? (
                        <EmptyHint dark={dark}>{tr('pflege.media.empty_media')}</EmptyHint>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 130 : 160}px, 1fr))`, gap: 12 }}>
                          {items.map(item => (
                            <MediaTile
                              key={item.id}
                              item={item}
                              dark={dark}
                              lang={lang}
                              alwaysShowActions={isMobile}
                              busy={busyKey === `media:${item.id}`}
                              onOpen={() => openItem(item)}
                              onDelete={canWrite ? () => { void remove(item); } : undefined}
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Dokumente */}
          {showDocCard && (!filtering || visibleDocs.length > 0) && (
            <Card dark={dark} style={{ padding: 0 }}>
              <WindowHeader dark={dark} title={tr('pflege.media.title_docs')} count={visibleDocs.length} />
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {docGroups.map(slug => {
                  const items = visibleDocs.filter(d => d.kategorie === slug);
                  if (filtering && items.length === 0) return null;
                  return (
                    <section key={slug} aria-label={categoryLabel(tr, 'document', slug, meta.role)}>
                      <Subtitle dark={dark}>
                        {categoryLabel(tr, 'document', slug, meta.role)} <span style={{ color: t.muted, fontWeight: 500 }}>· {items.length}</span>
                      </Subtitle>
                      {items.length === 0 ? (
                        <EmptyHint dark={dark}>{tr('pflege.media.empty_docs')}</EmptyHint>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {items.map(doc => (
                            <DocRow
                              key={doc.id}
                              item={doc}
                              dark={dark}
                              lang={lang}
                              isMobile={isMobile}
                              busy={busyKey === `document:${doc.id}`}
                              onOpen={() => openItem(doc)}
                              categoryControl={canWrite ? categorySelect(doc) : null}
                              onDelete={canWrite ? () => { void remove(doc); } : undefined}
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      ) : null}

      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          index={lightbox.index}
          onIndex={i => setLightbox(lb => (lb ? { ...lb, index: i } : lb))}
          onClose={() => setLightbox(null)}
          renderCategory={canWrite ? item => categorySelect(item, 'overlay') : undefined}
          onDelete={canWrite ? item => { void remove(item); } : undefined}
        />
      )}
    </div>
  );
}

/* ─── Bausteine ──────────────────────────────────────────────────────────── */

function WindowHeader({ title, count, dark }: { title: string; count: number; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  return (
    <div style={{
      padding: '14px 20px',
      borderBottom: `1px solid ${t.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>{title}</h3>
      <span style={{ fontSize: 12, color: t.muted }}>{tr('pflege.media.entries', { count })}</span>
    </div>
  );
}

function Subtitle({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: t.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      marginBottom: 8,
    }}>{children}</div>
  );
}

function EmptyHint({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic', padding: '8px 0' }}>{children}</div>
  );
}

function IconAction({
  label, onClick, href, danger, dark, children, disabled,
}: {
  label: string; onClick?: () => void; href?: string | null; danger?: boolean; dark: boolean;
  children: React.ReactNode; disabled?: boolean;
}) {
  const t = getThemeColors(dark);
  const style: React.CSSProperties = {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
    border: `1px solid ${danger ? '#fecdca' : t.border}`,
    background: danger ? (dark ? '#2b1414' : '#fef3f2') : t.card,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  };
  if (href) {
    return (
      <a href={href} aria-label={label} title={label} style={style} onClick={e => e.stopPropagation()}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} style={style}
      onClick={e => { e.stopPropagation(); onClick?.(); }}>
      {children}
    </button>
  );
}

function MediaTile({
  item, dark, lang, onOpen, onDelete, busy, alwaysShowActions,
}: {
  item: MediaDocItem; dark: boolean; lang: string; onOpen: () => void;
  onDelete?: () => void; busy: boolean; alwaysShowActions: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [hover, setHover] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const thumb = item.thumbnail_url ?? (item.is_image ? item.url : null);
  const showActions = alwaysShowActions || hover;

  let visual: React.ReactNode;
  if (item.processing) {
    visual = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: t.muted, fontSize: 11.5, padding: 8, textAlign: 'center' }}>
        <Spinner /> {tr('pflege.media.processing')}
      </div>
    );
  } else if (thumb && !imgFailed) {
    visual = (
      <img src={thumb} alt={item.name} loading="lazy" onError={() => setImgFailed(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    );
  } else if (item.is_video && item.url) {
    // Ohne Thumbnail: erstes Frame per <video preload="metadata">.
    visual = (
      <video src={`${item.url}#t=0.5`} preload="metadata" muted playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
    );
  } else {
    visual = <FileBadge name={item.name} size={44} />;
  }

  return (
    <div
      data-testid="media-tile"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        position: 'relative', border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden',
        background: t.card, opacity: busy ? 0.55 : 1, transition: 'box-shadow 0.15s',
        boxShadow: hover ? '0 4px 14px rgba(16,24,40,0.10)' : 'none',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={item.processing}
        aria-label={`${tr('pflege.media.preview')}: ${item.name}`}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', aspectRatio: '4 / 3', padding: 0, border: 'none',
          background: t.cardMuted, cursor: item.processing ? 'default' : 'zoom-in', overflow: 'hidden',
        }}
      >
        {visual}
        {item.is_video && !item.processing && <PlayBadge />}
      </button>

      <div style={{ padding: '8px 10px' }}>
        <div title={item.name} style={{ fontSize: 12.5, fontWeight: 500, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>
          {[formatFileSize(item.size), formatMediaDate(item.created_at, lang)].filter(Boolean).join(' · ')}
        </div>
      </div>

      {showActions && !item.processing && (
        <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
          {item.download_url && (
            <IconAction label={tr('pflege.media.download')} href={item.download_url} dark={dark}>
              <Icon.Download c={t.text} />
            </IconAction>
          )}
          {onDelete && (
            <IconAction label={tr('common.delete')} onClick={onDelete} danger dark={dark} disabled={busy}>
              <Icon.Trash c="#b42318" />
            </IconAction>
          )}
        </div>
      )}
    </div>
  );
}

function DocRow({
  item, dark, lang, isMobile, onOpen, onDelete, categoryControl, busy,
}: {
  item: MediaDocItem; dark: boolean; lang: string; isMobile: boolean; onOpen: () => void;
  onDelete?: () => void; categoryControl: React.ReactNode; busy: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const ext = fileExtension(item.name).toUpperCase();
  const uploader = item.uploaded_by?.type === 'customer'
    ? tr('pflege.media.uploaded_by_customer')
    : item.uploaded_by?.name ? tr('pflege.media.uploaded_by', { name: item.uploaded_by.name }) : null;
  const metaLine = [ext, formatFileSize(item.size), formatMediaDate(item.created_at, lang), uploader].filter(Boolean).join(' · ');

  return (
    <div
      data-testid="doc-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap',
        padding: '8px 10px', borderRadius: 10,
        border: `1px solid ${t.border}`, background: t.card, opacity: busy ? 0.55 : 1,
      }}
    >
      <FileBadge name={item.name} />
      <div style={{ flex: 1, minWidth: isMobile ? 'calc(100% - 60px)' : 0 }}>
        <button
          type="button"
          onClick={onOpen}
          disabled={!item.url}
          title={item.name}
          style={{
            display: 'block', maxWidth: '100%', padding: 0, border: 'none', background: 'transparent',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: item.url ? t.heading : t.muted,
            cursor: item.url ? 'pointer' : 'default', textAlign: 'left',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { if (item.url) e.currentTarget.style.color = BRAND; }}
          onMouseLeave={e => { e.currentTarget.style.color = item.url ? t.heading : t.muted; }}
        >
          {item.name}
        </button>
        <div style={{ fontSize: 11.5, color: t.muted, marginTop: 2 }}>{metaLine}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: isMobile ? 48 : 0 }}>
        {categoryControl}
        {item.download_url && (
          <IconAction label={tr('pflege.media.download')} href={item.download_url} dark={dark}>
            <Icon.Download c={t.text} />
          </IconAction>
        )}
        {onDelete && (
          <IconAction label={tr('common.delete')} onClick={onDelete} danger dark={dark} disabled={busy}>
            <Icon.Trash c="#b42318" />
          </IconAction>
        )}
      </div>
    </div>
  );
}
